import React, { useState, useEffect, useContext, useCallback } from 'react'

import { transformFileToBase64, transformBitsToMegabytes, openInNewTab } from 'utils/fileHelpers'
import { S3_TYPE_MAP, TYPE_MAP } from 'utils/mapping'

import { toast } from 'react-toastify'
import moment from 'moment'
import { isEmpty } from 'lodash'
import { UserContext } from './UserStore'
import { JobContext } from './JobStore'


import useJobApi from 'api/jobApi'
import { useTranslation } from 'react-i18next'
// import DialogModalStories from 'stories/components/form/DialogModal/DialogModal.stories'

const initialState = {
    isDocumentsLoading: false,
    fileToUpload: {},
    fileDescriptionToUpload: '',
    filePipeToUpload: '',
    fileIdToDownload: '',
    fileIdToDelete: '',
    isFileUploading: false,
    isFileDownloading: false,
    isFileDeleting: false,
    quickAccessDocuments: [],
    source: ''
}

export const JobDocumentsContext = React.createContext(initialState)

export const JobDocumentsProvider = ({ children }) => {
    const { idToken, currentUser, endUsers } = useContext(UserContext)
    const { jobId } = useContext(JobContext)
    const jobApi = useJobApi(idToken)
    const { t } = useTranslation()

    const [isDocumentsLoading, setIsDocumentsLoading] = useState(false)
    const [fileToUpload, setfileToUpload] = useState({})
    const [fileDescriptionToUpload, setFileDescriptionToUpload] = useState('')
    const [filePipeToUpload, setFilePipeToUpload] = useState('')
    const [fileIdToDownload, setFileIdToDownload] = useState('')
    const [fileIdToDelete, setFileIdToDelete] = useState('')
    const [isFileUploading, setIsFileUploading] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isFileDownloading, setIsFileDownloading] = useState(false)
    const [isFileDeleting, setIsFileDeleting] = useState(false)
    const [jobDocuments, setJobDocuments] = useState([])
    const [quickAccessDocuments, setQuickAccessDocument] = useState([])
    const [source, setSource] = useState('')
    const [jobData, setJobData] = useState()
    const [jobStoppages, setJobStoppages] = useState()
    const [tableData, setTableData] = useState([])
    // const [result, setResult] = useState(false)
    const [documentLoaded, setDocumentLoaded] = useState(false)
    const [isUploading, setIsUploading] = useState(false)

    const getFileDetails = useCallback((file, currentUser) => {
        let type = file.name.split(/\.(?=[^.]+$)/)[1]
        type = type.toLowerCase()
        if (S3_TYPE_MAP[type] && TYPE_MAP[type]) {
            const fileDetails = {
                name: file.name,
                jobId: jobId,
                description: fileDescriptionToUpload || '',
                pipe: filePipeToUpload || ' ',
                uploadedBy: currentUser.name,
                contentType: S3_TYPE_MAP[type] || '',
                fileType: TYPE_MAP[type] || '',
                size: transformBitsToMegabytes(file.size) + ' MB'
            }
            return fileDetails
        } else {
            return {}
        }

    }, [jobId, fileDescriptionToUpload, filePipeToUpload])

    const convertFile = useCallback((file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsArrayBuffer(file)
            reader.onload = (e) => {
                const byteArray = e.target.result
                const fileByteArray = byteArray
                const fileContent = transformFileToBase64(fileByteArray)
                const fileDetails = getFileDetails(file, currentUser)
                if (!isEmpty(fileDetails)) {
                    fileDetails.content = fileContent
                    resolve(fileDetails)
                } else {
                    reject(t("Format Invalid. Please upload a valid File."))
                }
            }
            reader.onerror = reject
        })
    }, [currentUser, getFileDetails, t])

    const formatDocuments = (documents) => {
        return documents.map(doc => ({ ...doc, uploadDate: moment(doc.uploadDate).format('YYYY-MM-DD') }))
    }

    const getJobs = useCallback(async () => {
        setIsLoading(true)
        try {
            const response = await jobApi.getJob(endUsers)
            setTableData(response.tableData)
            setIsLoading(false)
        } catch (error) {
            console.debug(error)
            setIsLoading(false)
        }
    }, [endUsers, jobApi])

    const createNewJob = async (formState) => {
        toast(t('upload.sendJob'))
        setIsUploading(true)
        const { error, data } = await jobApi.uploadJob(formState, jobData, jobStoppages, endUsers)

        if (error) {
            setIsUploading(false)
            return { error }
        }

        setTableData(data.tableData)
        setIsUploading(false)
        return { data }
    }

    const deleteJob = async (jobId) => {
        // toast(t('upload.sendJob'));
        setIsLoading(true)
        const response = await jobApi.deleteJob(jobId)
        setIsLoading(false)
        return response
    }

    /**
     * Effection for downloading a document
     */
    useEffect(() => {
        const hydrate = async () => {
            try {
                if (fileIdToDownload) {
                    setIsFileDownloading(true)
                    const response = await jobApi.getFileFromApi(fileIdToDownload)
                    openInNewTab(response)
                }
            } catch (error) {
                throw new Error(error)
            } finally {
                setIsFileDownloading(false)
            }
        }

        hydrate().catch(err => {
            toast.error(err.message)
            console.log(err)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileIdToDownload])

    /**
     * Effection for uploading a PRE RUNNING document
     */
    useEffect(() => {
        const hydrate = async () => {
            try {
                if (fileToUpload && fileToUpload.name && source) {

                    setIsFileUploading(true)
                    const processedFile = await convertFile(fileToUpload)
                    if (processedFile) {
                        // eslint-disable-next-line no-unused-vars
                        const response = await jobApi.uploadFileToApi(processedFile, source)

                        toast.success(t("upload.sendSuccess"))
                        const documents = await jobApi.getDocumentsFromApi(jobId, source)
                        if (documents && documents.list) {
                            const formatedDocuments = formatDocuments(documents.list)
                            setJobDocuments(formatedDocuments)
                            setQuickAccessDocument(formatedDocuments.slice(0, 4))
                        }
                    }
                }
            } catch (error) {
                throw new Error(error)
            } finally {
                setfileToUpload(null)
                setIsFileUploading(false)
            }
        }

        hydrate().catch(err => {
            toast.error(err.message)
            console.log(err)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileToUpload, jobId, convertFile, t])


    /**
     * Effection for deleting a PRE RUNNING document
     */
    useEffect(() => {
        const hydrate = async () => {
            try {
                if (fileIdToDelete) {
                    setIsFileDeleting(true)
                    await jobApi.deleteFileToApi(fileIdToDelete, source)
                    toast.success(t('upload.sendSuccess'))
                    const documents = await jobApi.getDocumentsFromApi(jobId, source)
                    if (documents && documents.list) {
                        const formatedDocuments = formatDocuments(documents.list)
                        setJobDocuments(formatedDocuments)
                        setQuickAccessDocument(formatedDocuments.slice(0, 4))
                    }
                }
            } catch (error) {
                throw new Error(error)
            } finally {
                setFileIdToDelete(null)
                setIsFileDeleting(false)
            }
        }

        hydrate().catch(err => {
            toast.error(err.message)
            console.log(err)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileIdToDelete, jobId, source, t])

    /**
    * Effection for fetching documents of a job
    */
    useEffect(() => {
        const hydrate = async () => {
            try {
                setIsDocumentsLoading(true)
                getJobs()
                if (jobId) {
                    const documents = await jobApi.getDocumentsFromApi(jobId, source)
                    if (documents && documents.list) {
                        const formatedDocuments = formatDocuments(documents.list)
                        setJobDocuments(formatedDocuments)
                        setQuickAccessDocument(formatedDocuments.slice(0, 4))
                    }
                }
            } catch (error) {
                setJobDocuments([])
                setQuickAccessDocument([])
                throw new Error(error)
            } finally {
                setIsDocumentsLoading(false)
            }
        }

        hydrate().catch(err => {
            toast.error(err.message)
            console.log(err)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, source])

    const renderStore =
        <JobDocumentsContext.Provider value={{
            fileToUpload,
            setfileToUpload,
            isFileUploading,
            jobDocuments,
            isDocumentsLoading,
            quickAccessDocuments,
            fileIdToDownload,
            setFileIdToDownload,
            isFileDownloading,
            setFileDescriptionToUpload,
            setFilePipeToUpload,
            setFileIdToDelete,
            isFileDeleting,
            source,
            setSource,
            setJobData,
            setJobStoppages,
            createNewJob,
            getJobs,
            tableData,
            setTableData,
            documentLoaded,
            setDocumentLoaded,
            isUploading,
            deleteJob,
            isLoading,
            jobData
        }}>
            {children}
        </JobDocumentsContext.Provider>

    return renderStore
}
